import React, { useState } from "react";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import XGSNavLink from "../xgs-nav-link/xgsNavLink";
import { Routes } from "../../app/route/RoutesConfig";
import Loading from "../loading/loading";
import { useTerminalDetails, TerminalDetailsResult } from "./useTerminalDetails";
import "./terminalCard.scss";

export interface TerminalCardProps {
  terminalNumber: number;
  terminalName?: string;
  terminalAirportCode?: string;
  size?: "sm" | "md";
  showHoverCard?: boolean;
  /** When false, hover popover can extend outside scroll containers (e.g. drawer tables) */
  keepTooltipInside?: boolean;
  className?: string;
}

function formatAddress(details: TerminalDetailsResult | null | undefined): string | null {
  if (!details?.address) return null;
  const { physicalAddress, city, stateProvince, zipPostalCode } = details.address;
  const line2 = [city, stateProvince, zipPostalCode].filter(Boolean).join(", ");
  return [physicalAddress, line2].filter(Boolean).join(", ") || null;
}

interface PopoverContentProps {
  terminalNumber: number;
  fallbackName?: string;
  fallbackAirportCode?: string;
  details: TerminalDetailsResult | null | undefined;
  isLoading: boolean;
}

function formatTerminalType(t: TerminalDetailsResult["terminalType"]): string | null {
  if (!t) return null;
  const type = t.type?.trim();
  const desc = t.description?.trim();
  if (type && desc && type !== desc) return `${type} (${desc})`;
  return type || desc || null;
}

const PhoneIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z" />
  </svg>
);

const MailIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const ContactRow: React.FC<{ icon: React.ReactNode; value: string; href?: string }> = ({
  icon,
  value,
  href,
}) => (
  <div className="xgs-terminal-card-popover__contact-row">
    <span className="xgs-terminal-card-popover__contact-icon">{icon}</span>
    {href ? (
      <a className="xgs-terminal-card-popover__contact-value" href={href}>
        {value}
      </a>
    ) : (
      <span className="xgs-terminal-card-popover__contact-value">{value}</span>
    )}
  </div>
);

const PopoverContent: React.FC<PopoverContentProps> = ({
  terminalNumber,
  fallbackName,
  fallbackAirportCode,
  details,
  isLoading,
}) => {
  const name = details?.terminalName ?? fallbackName ?? `Terminal ${terminalNumber}`;
  const airportCode = details?.airportCode ?? fallbackAirportCode;
  const address = formatAddress(details);
  const status = details?.status;
  const region = details?.region?.regionName;
  const terminalType = formatTerminalType(details?.terminalType);

  const terminalPhoneRaw = details?.contact?.phoneNumber ?? null;
  const terminalPhone = terminalPhoneRaw ? terminalPhoneRaw.formatPhone() : null;
  const terminalEmail = details?.contact?.terminalEmail ?? null;

  const managerName = details?.contact?.managerName ?? null;
  const managerPhoneRaw = details?.contact?.managerPhone ?? null;
  const managerPhone = managerPhoneRaw ? managerPhoneRaw.formatPhone() : null;
  const managerEmail = details?.contact?.managerEmail ?? null;

  const hasTerminalContact = !!(terminalPhone || terminalEmail);
  const hasManagerContact = !!(managerName || managerPhone || managerEmail);
  const hasAnyDetail = !!(
    status ||
    region ||
    terminalType ||
    address ||
    hasTerminalContact ||
    hasManagerContact
  );

  return (
    <div className="xgs-terminal-card-popover__content">
      <div className="xgs-terminal-card-popover__header">
        <span className="xgs-terminal-card-popover__title">
          {airportCode ? `${airportCode} - ${name}` : name}
        </span>
        <span className="xgs-terminal-card-popover__subtitle">#{terminalNumber}</span>
      </div>
      <div className="xgs-terminal-card-popover__divider" />
      {isLoading && !details ? (
        <div className="xgs-terminal-card-popover__loading">
          <Loading isLoading={true} />
        </div>
      ) : !hasAnyDetail ? (
        <div className="xgs-terminal-card-popover__loading">
          No additional terminal details available.
        </div>
      ) : (
        <>
          {(status || region || terminalType || address) && (
            <div className="xgs-terminal-card-popover__body">
              {status && (
                <>
                  <span className="xgs-terminal-card-popover__label">Status</span>
                  <span className="xgs-terminal-card-popover__value">{status}</span>
                </>
              )}
              {terminalType && (
                <>
                  <span className="xgs-terminal-card-popover__label">Type</span>
                  <span className="xgs-terminal-card-popover__value">{terminalType}</span>
                </>
              )}
              {region && (
                <>
                  <span className="xgs-terminal-card-popover__label">Region</span>
                  <span className="xgs-terminal-card-popover__value">{region}</span>
                </>
              )}
              {address && (
                <>
                  <span className="xgs-terminal-card-popover__label">Address</span>
                  <span className="xgs-terminal-card-popover__value">{address}</span>
                </>
              )}
            </div>
          )}

          {hasTerminalContact && (
            <>
              <div className="xgs-terminal-card-popover__divider" />
              <div className="xgs-terminal-card-popover__section">
                <div className="xgs-terminal-card-popover__section-title">Terminal Contact</div>
                {terminalPhone && (
                  <ContactRow
                    icon={<PhoneIcon />}
                    value={terminalPhone}
                    href={`tel:${terminalPhoneRaw?.unformatPhone?.() ?? terminalPhoneRaw}`}
                  />
                )}
                {terminalEmail && (
                  <ContactRow icon={<MailIcon />} value={terminalEmail} href={`mailto:${terminalEmail}`} />
                )}
              </div>
            </>
          )}

          {hasManagerContact && (
            <>
              <div className="xgs-terminal-card-popover__divider" />
              <div className="xgs-terminal-card-popover__section">
                <div className="xgs-terminal-card-popover__section-title">
                  Manager{managerName ? ` · ${managerName}` : ""}
                </div>
                {managerPhone && (
                  <ContactRow
                    icon={<PhoneIcon />}
                    value={managerPhone}
                    href={`tel:${managerPhoneRaw?.unformatPhone?.() ?? managerPhoneRaw}`}
                  />
                )}
                {managerEmail && (
                  <ContactRow icon={<MailIcon />} value={managerEmail} href={`mailto:${managerEmail}`} />
                )}
              </div>
            </>
          )}
        </>
      )}
      <div className="xgs-terminal-card-popover__footer">
        <XGSNavLink
          className="xgs-terminal-card-popover__more-link"
          to={`${Routes.terminalSummary.list}/${terminalNumber}`}
        >
          Show more →
        </XGSNavLink>
      </div>
    </div>
  );
};

const TerminalCard: React.FC<TerminalCardProps> = ({
  terminalNumber,
  terminalName,
  terminalAirportCode,
  size = "sm",
  showHoverCard = true,
  keepTooltipInside = true,
  className,
}) => {
  const [shouldFetch, setShouldFetch] = useState<boolean>(!terminalName);

  const { data: details, isLoading } = useTerminalDetails(terminalNumber, shouldFetch);

  const displayName = terminalName ?? details?.terminalName ?? `Terminal ${terminalNumber}`;
  const airportCode = terminalAirportCode ?? details?.airportCode ?? undefined;

  const trigger = (
    <span
      className={`xgs-terminal-card${size === "sm" ? " xgs-terminal-card--sm" : ""}${
        className ? ` ${className}` : ""
      }`}
      tabIndex={0}
    >
      <span className="xgs-terminal-card__name">
        {airportCode ? `${airportCode} - ${displayName}` : displayName}
      </span>
      <span className="xgs-terminal-card__id">{terminalNumber}</span>
    </span>
  );

  if (!showHoverCard) return trigger;

  return (
    <Popup
      trigger={trigger}
      position={["bottom left", "bottom right", "top left"]}
      on={["hover", "focus"]}
      arrow={false}
      mouseEnterDelay={150}
      mouseLeaveDelay={120}
      keepTooltipInside={keepTooltipInside}
      onOpen={() => setShouldFetch(true)}
      contentStyle={{
        width: "296px",
        padding: 0,
        borderRadius: 4,
        border: "1px solid #E0E0E0",
        boxShadow: "0px 1px 6px rgba(0, 0, 0, 0.15)",
        zIndex: keepTooltipInside ? 22 : 1100,
      }}
    >
      <PopoverContent
        terminalNumber={terminalNumber}
        fallbackName={terminalName}
        fallbackAirportCode={terminalAirportCode}
        details={details}
        isLoading={isLoading}
      />
    </Popup>
  );
};

export default TerminalCard;
